import {
  Box,
  Button,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  CheckCircleOutline as CheckCircleOutlineIcon,
  ErrorOutline as ErrorOutlineIcon,
  AccessTime as AccessTimeIcon,
  AccountBalance as AccountBalanceIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import ResponsiveDialog from 'app/shared-components/layout/dialog/ResponsiveDialog';
import ResponsiveDialogActions from 'app/shared-components/layout/dialog/ResponsiveDialogActions';

export type BankLinkingDialogState =
  | { type: 'closed' }
  | { type: 'loading'; message: string }
  | { type: 'success'; accounts: Array<{ name: string; mask: string; type: string }> }
  | { type: 'pending'; accounts: Array<{ name: string; mask: string; type: string }> }
  | {
      type: 'mixed';
      successAccounts: Array<{ name: string; mask: string; type: string }>;
      pendingAccounts: Array<{ name: string; mask: string; type: string }>;
    }
  | { type: 'already_linked'; accounts: Array<{ name: string; mask: string; type: string }> }
  | { type: 'already_pending'; accounts: Array<{ name: string; mask: string; type: string }> }
  | { type: 'error'; message?: string };

interface BankAccountLinkingDialogsProps {
  state: BankLinkingDialogState;
  onClose: () => void;
  onSuccessClose?: () => void;
  /** Text for the page name in pending dialog (e.g., "credit management page" or "debit funding page") */
  returnPageName?: string;
}

function BankAccountLinkingDialogs({
  state,
  onClose,
  onSuccessClose,
  returnPageName = 'credit management page',
}: BankAccountLinkingDialogsProps) {
  if (state.type === 'closed') {
    return null;
  }

  // Loading Dialog
  if (state.type === 'loading') {
    return (
      <ResponsiveDialog open maxWidth="sm" fullWidth>
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress size={64} thickness={4} sx={{ mb: 3 }} />
          <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
            {state.message}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please wait while we set up your account...
          </Typography>
        </Box>
      </ResponsiveDialog>
    );
  }

  // Success Dialog
  if (state.type === 'success') {
    return (
      <ResponsiveDialog
        open
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
        sx={{
          '& .MuiDialog-paper': {
            marginTop: { xs: '-2vh', sm: '-2vh' },
            borderRadius: '20px',
          },
        }}
      >
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Box display="flex" justifyContent="center" mb={2}>
            <Box
              sx={{
                width: 88,
                height: 88,
                borderRadius: '50%',
                background: 'linear-gradient(180deg, rgba(76,175,80,0.12) 0%, rgba(76,175,80,0.08) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CheckCircleOutlineIcon sx={{ fontSize: 48, color: 'success.main' }} />
            </Box>
          </Box>
          <Typography variant="h5" fontWeight={700} color="success.main" sx={{ mb: 1 }}>
            {state.accounts.length === 1 ? 'Bank Account Linked!' : 'Bank Accounts Linked!'}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {state.accounts.length === 1
              ? 'Your bank account has been successfully linked and is ready for payments.'
              : `${state.accounts.length} bank accounts have been successfully linked and are ready for payments.`}
          </Typography>

          <List sx={{ textAlign: 'left', bgcolor: 'grey.50', borderRadius: 2, mb: 2 }}>
            {state.accounts.map((account, index) => (
              <ListItem key={index} sx={{ py: 1 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <AccountBalanceIcon color="success" />
                </ListItemIcon>
                <ListItemText
                  primary={account.name}
                  secondary={`${account.type.charAt(0).toUpperCase() + account.type.slice(1)} ••••${
                    account.mask
                  }`}
                />
                <CheckCircleOutlineIcon color="success" fontSize="small" />
              </ListItem>
            ))}
          </List>
        </Box>
        <ResponsiveDialogActions sx={{ p: 3, pt: 0, justifyContent: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={onSuccessClose ?? onClose}
            sx={{
              minWidth: 160,
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '1.4rem',
              py: 1.25,
            }}
          >
            Done
          </Button>
        </ResponsiveDialogActions>
      </ResponsiveDialog>
    );
  }

  // Pending Dialog (micro-deposit verification needed)
  if (state.type === 'pending') {
    return (
      <ResponsiveDialog
        open
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            marginTop: { xs: '-2vh', sm: '-2vh' },
            borderRadius: '20px',
          },
        }}
      >
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Box display="flex" justifyContent="center" mb={2}>
            <Box
              sx={{
                width: 88,
                height: 88,
                borderRadius: '50%',
                background: 'linear-gradient(180deg, rgba(255,193,7,0.15) 0%, rgba(255,193,7,0.08) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AccessTimeIcon sx={{ fontSize: 48, color: '#FFC107' }} />
            </Box>
          </Box>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 1, color: '#FFC107' }}>
            Verification Pending
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Your bank account is almost ready! A small deposit will appear in your bank statement within 1-2
            business days with a 3-letter code.
          </Typography>

          <List sx={{ textAlign: 'left', bgcolor: 'warning.lighter', borderRadius: 2, mb: 2 }}>
            {state.accounts.map((account, index) => (
              <ListItem key={index} sx={{ py: 1 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <AccountBalanceIcon sx={{ color: '#FFC107' }} />
                </ListItemIcon>
                <ListItemText
                  primary={account.name}
                  secondary={`${account.type.charAt(0).toUpperCase() + account.type.slice(1)} ••••${
                    account.mask
                  }`}
                />
                <AccessTimeIcon sx={{ color: '#FFC107' }} fontSize="small" />
              </ListItem>
            ))}
          </List>

          <Box sx={{ textAlign: 'left', mt: 1, pl: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2, pl: 3 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: '1.5rem',
                  flexShrink: 0,
                  mr: 1.5,
                }}
              >
                1
              </Box>
              <Typography variant="body1" color="text.secondary" sx={{ pt: 0.25 }}>
                Check your bank statement in 1-2 business days
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2, pl: 3 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: '1.5rem',
                  flexShrink: 0,
                  mr: 1.5,
                }}
              >
                2
              </Box>
              <Typography variant="body1" color="text.secondary" sx={{ pt: 0.25 }}>
                Find the deposit with a 3-letter code (e.g., "ABC")
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-start', pl: 3 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: '1.5rem',
                  flexShrink: 0,
                  mr: 1.5,
                }}
              >
                3
              </Box>
              <Typography variant="body1" color="text.secondary" sx={{ pt: 0.25 }}>
                Return to the {returnPageName} to enter the code
              </Typography>
            </Box>
          </Box>
        </Box>
        <ResponsiveDialogActions sx={{ mt: 0, p: 3, pt: 2, justifyContent: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={onSuccessClose ?? onClose}
            sx={{
              minWidth: 160,
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '1.4rem',
              py: 1.25,
            }}
          >
            Got It
          </Button>
        </ResponsiveDialogActions>
      </ResponsiveDialog>
    );
  }

  // Mixed Dialog (some success, some pending)
  if (state.type === 'mixed') {
    return (
      <ResponsiveDialog
        open
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            marginTop: { xs: '-2vh', sm: '-2vh' },
            borderRadius: '20px',
          },
        }}
      >
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Box display="flex" justifyContent="center" mb={2}>
            <Box
              sx={{
                width: 88,
                height: 88,
                borderRadius: '50%',
                background: 'linear-gradient(180deg, rgba(76,175,80,0.12) 0%, rgba(76,175,80,0.08) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CheckCircleOutlineIcon sx={{ fontSize: 48, color: 'success.main' }} />
            </Box>
          </Box>
          <Typography variant="h5" fontWeight={700} color="primary" sx={{ mb: 1 }}>
            Accounts Processed
          </Typography>

          {/* Successfully linked accounts */}
          {state.successAccounts.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="success.main" sx={{ mb: 1, textAlign: 'left' }}>
                Ready for Payments ({state.successAccounts.length})
              </Typography>
              <List sx={{ textAlign: 'left', bgcolor: 'grey.50', borderRadius: 2 }}>
                {state.successAccounts.map((account, index) => (
                  <ListItem key={index} sx={{ py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <AccountBalanceIcon color="success" />
                    </ListItemIcon>
                    <ListItemText
                      primary={account.name}
                      secondary={`${account.type.charAt(0).toUpperCase() + account.type.slice(1)} ••••${
                        account.mask
                      }`}
                    />
                    <CheckCircleOutlineIcon color="success" fontSize="small" />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Pending accounts */}
          {state.pendingAccounts.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ color: 'warning.main', mb: 1, textAlign: 'left' }}>
                Pending Verification ({state.pendingAccounts.length})
              </Typography>
              <List sx={{ textAlign: 'left', bgcolor: 'warning.lighter', borderRadius: 2, mb: 2 }}>
                {state.pendingAccounts.map((account, index) => (
                  <ListItem key={index} sx={{ py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <AccountBalanceIcon sx={{ color: 'warning.main' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={account.name}
                      secondary={`${account.type.charAt(0).toUpperCase() + account.type.slice(1)} ••••${
                        account.mask
                      }`}
                    />
                    <AccessTimeIcon sx={{ color: 'warning.main' }} fontSize="small" />
                  </ListItem>
                ))}
              </List>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                Check your bank statement in 1-2 days for a 3-letter verification code, then return to
                complete verification.
              </Typography>
            </Box>
          )}
        </Box>
        <ResponsiveDialogActions sx={{ p: 3, pt: 2, justifyContent: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={onSuccessClose ?? onClose}
            sx={{
              minWidth: 160,
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '1.4rem',
              py: 1.25,
            }}
          >
            Done
          </Button>
        </ResponsiveDialogActions>
      </ResponsiveDialog>
    );
  }

  // Already Linked Dialog
  if (state.type === 'already_linked') {
    return (
      <ResponsiveDialog
        open
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            marginTop: { xs: '-2vh', sm: '-2vh' },
            borderRadius: '20px',
          },
        }}
      >
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Box display="flex" justifyContent="center" mb={2}>
            <Box
              sx={{
                width: 88,
                height: 88,
                borderRadius: '50%',
                background: 'linear-gradient(180deg, rgba(33,150,243,0.12) 0%, rgba(33,150,243,0.08) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <InfoIcon sx={{ fontSize: 48, color: 'info.main' }} />
            </Box>
          </Box>
          <Typography variant="h5" fontWeight={700} color="primary" sx={{ mb: 1 }}>
            {state.accounts.length === 1 ? 'Account Already Linked' : 'Accounts Already Linked'}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {state.accounts.length === 1
              ? 'This bank account is already connected to your profile.'
              : 'These bank accounts are already connected to your profile.'}
          </Typography>

          <List sx={{ textAlign: 'left', bgcolor: 'grey.50', borderRadius: 2, mb: 2 }}>
            {state.accounts.map((account, index) => (
              <ListItem key={index} sx={{ py: 1 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <AccountBalanceIcon color="info" />
                </ListItemIcon>
                <ListItemText
                  primary={account.name}
                  secondary={`${account.type.charAt(0).toUpperCase() + account.type.slice(1)} ••••${
                    account.mask
                  }`}
                />
                <CheckCircleOutlineIcon color="info" fontSize="small" />
              </ListItem>
            ))}
          </List>
        </Box>
        <ResponsiveDialogActions sx={{ p: 3, pt: 0, justifyContent: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={onClose}
            sx={{
              minWidth: 160,
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '1.4rem',
              py: 1.25,
            }}
          >
            OK
          </Button>
        </ResponsiveDialogActions>
      </ResponsiveDialog>
    );
  }

  // Already Pending Dialog
  if (state.type === 'already_pending') {
    return (
      <ResponsiveDialog
        open
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            marginTop: { xs: '-2vh', sm: '-2vh' },
            borderRadius: '20px',
          },
        }}
      >
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Box display="flex" justifyContent="center" mb={2}>
            <Box
              sx={{
                width: 88,
                height: 88,
                borderRadius: '50%',
                background: 'linear-gradient(180deg, rgba(255,152,0,0.12) 0%, rgba(255,152,0,0.08) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AccessTimeIcon sx={{ fontSize: 48, color: 'warning.main' }} />
            </Box>
          </Box>
          <Typography variant="h5" fontWeight={700} color="primary" sx={{ mb: 1 }}>
            {state.accounts.length === 1 ? 'Account Pending Verification' : 'Accounts Pending Verification'}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {state.accounts.length === 1
              ? 'This bank account is already pending verification. Check your bank statement for the verification code.'
              : 'These bank accounts are already pending verification. Check your bank statements for the verification codes.'}
          </Typography>

          <List sx={{ textAlign: 'left', bgcolor: 'warning.lighter', borderRadius: 2, mb: 2 }}>
            {state.accounts.map((account, index) => (
              <ListItem key={index} sx={{ py: 1 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <AccountBalanceIcon sx={{ color: 'warning.main' }} />
                </ListItemIcon>
                <ListItemText
                  primary={account.name}
                  secondary={`${account.type.charAt(0).toUpperCase() + account.type.slice(1)} ••••${
                    account.mask
                  }`}
                />
                <AccessTimeIcon sx={{ color: 'warning.main' }} fontSize="small" />
              </ListItem>
            ))}
          </List>
        </Box>
        <ResponsiveDialogActions sx={{ p: 3, pt: 0, justifyContent: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={onClose}
            sx={{
              minWidth: 160,
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '1.4rem',
              py: 1.25,
            }}
          >
            OK
          </Button>
        </ResponsiveDialogActions>
      </ResponsiveDialog>
    );
  }

  // Error Dialog
  if (state.type === 'error') {
    return (
      <ResponsiveDialog
        open
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            marginTop: { xs: '-2vh', sm: '-2vh' },
            borderRadius: '20px',
          },
        }}
      >
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Box display="flex" justifyContent="center" mb={2}>
            <Box
              sx={{
                width: 88,
                height: 88,
                borderRadius: '50%',
                background: 'linear-gradient(180deg, rgba(229,57,53,0.12) 0%, rgba(229,57,53,0.08) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ErrorOutlineIcon sx={{ fontSize: 48, color: 'error.main' }} />
            </Box>
          </Box>
          <Typography variant="h5" fontWeight={700} color="primary" sx={{ mb: 1 }}>
            Unable to Link Account
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            An error occurred while setting up your bank account. Please try again or contact support for
            assistance.
          </Typography>
        </Box>
        <ResponsiveDialogActions sx={{ p: 3, pt: 0, justifyContent: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={onClose}
            sx={{
              minWidth: 160,
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '1.4rem',
              py: 1.25,
            }}
          >
            OK
          </Button>
        </ResponsiveDialogActions>
      </ResponsiveDialog>
    );
  }

  return null;
}

export default BankAccountLinkingDialogs;
